import React, { useEffect } from 'react'
import { Avatar, Button, Popconfirm, Table, Tag } from 'antd'
import { DeleteOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons'
import { useGetTeamMembers, useRemoveTeamMember } from 'api/Team/Team'
import { TeamMemberRecord } from 'types/team/team'
import { teamStore } from 'state/team/team'

interface MembersTabProps {
  teamId: string
}

const TYPE_COLORS: Record<string, string> = {
  guardian: 'blue', 'authorized-adult': 'cyan', organizer: 'purple', mentor: 'gold', kid: 'green',
}

const MembersTab: React.FC<MembersTabProps> = ({ teamId }) => {
  const { setTeamLeadModalVisible, setCoachModalVisible, setMemberModalVisible } = teamStore(
    store => ({
      setTeamLeadModalVisible: store.setTeamLeadModalVisible,
      setCoachModalVisible: store.setCoachModalVisible,
      setMemberModalVisible: store.setMemberModalVisible,
    }),
  )

  const { data: membersData, isLoading, refetch } = useGetTeamMembers(teamId, {})
  const { mutate: removeMember } = useRemoveTeamMember()

  useEffect(() => {
    refetch()
  }, [teamId])

  const handleRemove = (memberId: string) => {
    removeMember({ memberId, teamId }, { onSuccess: () => refetch() })
  }

  const makeColumns = () => [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: TeamMemberRecord) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar src={record.member?.photo?.thumb} icon={<UserOutlined />} size={36} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.member?.firstName} {record.member?.lastName}
            </div>
            {record.member?.email && (
              <div style={{ color: '#939598', fontSize: 12 }}>{record.member.email}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_: any, record: TeamMemberRecord) =>
        record.type ? (
          <Tag color={TYPE_COLORS[record.type] ?? 'default'}>{record.type}</Tag>
        ) : null,
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_: any, record: TeamMemberRecord) => (
        <Popconfirm
          title="Remove this member?"
          onConfirm={() => handleRemove(record.memberId)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      ),
    },
  ]

  const records = membersData?.records ?? []
  const leads = records.filter(r => r.role === 'lead')
  const coaches = records.filter(
    r => r.role === 'player' && (r.type === 'organizer' || r.type === 'mentor'),
  )
  const members = records.filter(
    r => r.role === 'player' && r.type !== 'organizer' && r.type !== 'mentor',
  )

  const sectionStyle = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    marginBottom: 20, border: '1px solid #EBEDF1',
  }

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
  }

  const addBtn = (onClick: () => void) => (
    <Button
      icon={<PlusOutlined />}
      onClick={onClick}
      style={{ borderColor: '#2BA7CA', color: '#2BA7CA', borderRadius: 8 }}
    >
      Add
    </Button>
  )

  return (
    <div style={{ padding: '24px 30px' }}>
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Team Leads</h3>
          {addBtn(() => setTeamLeadModalVisible(true))}
        </div>
        <Table
          dataSource={leads} columns={makeColumns()} rowKey="_id"
          loading={isLoading} pagination={false} showHeader={false}
          locale={{ emptyText: 'No team leads assigned' }}
        />
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Coaches</h3>
          {addBtn(() => setCoachModalVisible(true))}
        </div>
        <Table
          dataSource={coaches} columns={makeColumns()} rowKey="_id"
          loading={isLoading} pagination={false} showHeader={false}
          locale={{ emptyText: 'No coaches assigned' }}
        />
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Members</h3>
          {addBtn(() => setMemberModalVisible(true))}
        </div>
        <Table
          dataSource={members} columns={makeColumns()} rowKey="_id"
          loading={isLoading} pagination={false} showHeader={false}
          locale={{ emptyText: 'No members assigned' }}
        />
      </div>
    </div>
  )
}

export default MembersTab
